import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: 'Visit Us', lines: ['Inside Chester Square Shopping Centre', 'Opposite of Friand Bakery'] },
    { icon: Phone, title: 'Call Us', lines: ['+61 413 419 341'] },
    { icon: Mail, title: 'Email Us', lines: ['qasrparfumerie@gmail.com'] },
    { icon: Clock, title: 'Hours', lines: ['Mon - Sat: 9AM - 5PM', 'Sunday: 10AM - 4PM'] },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 arabian-pattern opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">تواصل معنا</p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-wide text-white mb-4">
            GET IN <span className="text-[#D4AF37]">TOUCH</span>
          </h1>
          <p className="font-body text-white/60 max-w-xl mx-auto">
            We would love to hear from you. Reach out for inquiries, appointments, or to learn more about our fragrances.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            {contactInfo.map((info) => (
              <div key={info.title} className="p-6 border border-[#D4AF37]/20 bg-[#050505] text-center">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#D4AF37]/30 flex items-center justify-center">
                  <info.icon size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-sm tracking-wide text-white mb-2">{info.title}</h3>
                {info.lines.map((line, idx) => (
                  <p key={idx} className="font-body text-white/50 text-sm">{line}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="font-heading text-xs tracking-[0.3em] text-[#D4AF37] mb-4">SEND US A MESSAGE</p>
            <h2 className="font-heading text-3xl tracking-wide text-white">
              WE'D LOVE TO <span className="text-[#D4AF37]">HEAR FROM YOU</span>
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">NAME</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="contact-name-input"
                  className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body"
                  required
                />
              </div>
              <div>
                <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">EMAIL</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contact-email-input"
                  className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body"
                  required
                />
              </div>
            </div>

            <div>
              <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">SUBJECT</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                data-testid="contact-subject-input"
                className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body"
                required
              />
            </div>

            <div>
              <label className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-2 block">MESSAGE</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                data-testid="contact-message-input"
                rows={6}
                className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 focus:border-[#D4AF37] text-white px-4 py-4 outline-none font-body resize-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="w-full bg-[#D4AF37] text-[#050505] py-5 font-heading text-sm tracking-[0.2em] hover:bg-[#E5C158] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
            >
              <Send size={18} />
              {loading ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
